import { App, Avatar, Button, Listy, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { BatchOperationEnum } from '../../enums/BatchOperationEnum';
import { BatchStatusEnum } from '../../enums/BatchStatusEnum';
import useFormatTimestamp from '../../hooks/useFormatTimestamp';
import type { Batch, ClassificationAdditionalData } from '../../interfaces/Batch';
import { BatchService } from '../../services/batchApiService';
import { getApiErrorCode, getApiErrorField } from '../../utils/apiErrorCode';
import { dateFromTicks } from '../../utils/utils';
import ListyMetaRow from '../Common/ListyMetaRow';
import { LISTY_ITEM_NO_HOVER } from '../Common/listyItemClassNames';
import { Icon } from '../Core/Icon';
import BatchDetailForm from '../Forms/BatchDetailForm';

const batchService = new BatchService();

interface BatchesListProps {
  batches: Batch[];
  onRefresh?: () => void;
}

/** Cancel UI only for Classification batches still Processing (BE gate). */
export function canCancelBatch(batch: Pick<Batch, 'operation' | 'status'>): boolean {
  return (
    batch.operation === BatchOperationEnum.Classification &&
    batch.status === BatchStatusEnum.Processing
  );
}

export function resolveBatchActionErrorMessage(
  t: (key: string) => string,
  error: unknown,
  fallbackKey: string,
): string {
  const response = (error as { response?: unknown })?.response;
  const code = getApiErrorCode(response);
  switch (code) {
    case ApiErrorEnum.BatchNotFound:
      return t('errors.batchNotFound');
    case ApiErrorEnum.BatchNotCancellable:
      // status → already finished; operation → wrong batch type
      if (getApiErrorField(response, 'status')) {
        return t('errors.batchAlreadyFinished');
      }
      return t('errors.batchNotCancellable');
    case ApiErrorEnum.BatchRetryNotSupported:
      return t('errors.batchRetryNotSupported');
    case ApiErrorEnum.NoFileJobsFound:
      return t('errors.noFileJobsFound');
    case ApiErrorEnum.QueueUnavailable:
      return t('errors.queueUnavailable');
    default:
      return t(fallbackKey);
  }
}

function shouldRefreshListAfterBatchError(error: unknown): boolean {
  const code = getApiErrorCode((error as { response?: unknown })?.response);
  return code === ApiErrorEnum.BatchNotFound || code === ApiErrorEnum.BatchNotCancellable;
}

/**
 * Main component for displaying the list of batch operations along with their status, operations, and controls.
 */
export default function BatchesList({ batches, onRefresh }: BatchesListProps) {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const formatTimestamp = useFormatTimestamp();

  const hasFailedBatches = batches.some((batch) => batch.status === BatchStatusEnum.Failed);

  const handleCancelAllFailed = (): void => {
    modal.confirm({
      title: t('modal.batchesList.cancelAllFailedConfirmTitle'),
      content: t('modal.batchesList.cancelAllFailedConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true, style: { minWidth: 80 } },
      cancelButtonProps: { style: { minWidth: 80 } },
      styles: { body: { overflow: 'hidden' } },
      onOk: () =>
        batchService
          .cancelAllFailedBatches()
          .then(() => onRefresh?.())
          .catch((error: unknown) => {
            message.error(
              resolveBatchActionErrorMessage(
                t,
                error,
                'modal.batchesList.cancelAllFailedConfirmTitle',
              ),
            );
          }),
    });
  };

  const handleCancelBatch = (e: React.MouseEvent, batch: Batch): void => {
    e.stopPropagation();
    modal.confirm({
      title: t('modal.batchesList.cancelBatchConfirmTitle'),
      content: t('modal.batchesList.cancelBatchConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true, style: { minWidth: 80 } },
      cancelButtonProps: { style: { minWidth: 80 } },
      styles: { body: { overflow: 'hidden' } },
      onOk: () =>
        batchService
          .cancelBatch(batch.id)
          .then(() => onRefresh?.())
          .catch((error: unknown) => {
            message.error(
              resolveBatchActionErrorMessage(t, error, 'modal.batchesList.cancelBatchConfirmTitle'),
            );
            if (shouldRefreshListAfterBatchError(error)) {
              onRefresh?.();
            }
          }),
    });
  };

  const handleBatchClick = (batch: Batch): void => {
    const canOpenDetail =
      batch.status === BatchStatusEnum.Failed ||
      batch.status === BatchStatusEnum.Processing ||
      batch.status === BatchStatusEnum.Queued ||
      batch.status === BatchStatusEnum.PartiallyCompleted;

    if (!canOpenDetail) return;

    const detailModal = modal.confirm({
      title: (
        <div className="flex items-center gap-2">
          <Icon name="AiFileIcon" className="text-blue-dark h-5 w-5" />
          <span>{t('modal.batchDetailForm.title')}</span>
        </div>
      ),
      icon: null,
      content: (
        <BatchDetailForm
          batchId={batch.id}
          batch={batch}
          onListRefresh={onRefresh}
          onClose={() => detailModal.destroy()}
        />
      ),
      footer: null,
      width: 720,
    });
  };

  const getOperationIcon = (
    operation: BatchOperationEnum,
  ): 'AiFileIcon' | 'FolderIcon' | 'TrashIcon' | 'FilesIcon' => {
    switch (operation) {
      case BatchOperationEnum.Classification:
        return 'AiFileIcon';
      case BatchOperationEnum.FolderExpansion:
        return 'FolderIcon';
      case BatchOperationEnum.Delete:
        return 'TrashIcon';
      default:
        return 'FilesIcon';
    }
  };

  const getObjectNames = (batch: Batch): string[] => {
    if (batch.operation !== BatchOperationEnum.Classification || !batch.additionalData) {
      return [];
    }
    try {
      const data: ClassificationAdditionalData = JSON.parse(batch.additionalData);
      return data.objectNames || [];
    } catch {
      return [];
    }
  };

  const getStatusColor = (status: BatchStatusEnum): string => {
    switch (status) {
      case BatchStatusEnum.Queued:
        return 'text-gray-dark';
      case BatchStatusEnum.Processing:
        return 'text-blue-dark';
      case BatchStatusEnum.Completed:
        return 'text-green';
      case BatchStatusEnum.Failed:
        return 'text-red-dark';
      case BatchStatusEnum.PartiallyCompleted:
        return 'text-yellow-dark';
      case BatchStatusEnum.Cancelled:
        return 'text-gray-dark';
      default:
        return 'text-gray-dark';
    }
  };

  const getStatusIcon = (status: BatchStatusEnum) => {
    switch (status) {
      case BatchStatusEnum.Queued:
        return <Icon name="CalendarClockIcon" className="h-4 w-4" />;
      case BatchStatusEnum.Processing:
        return <Icon name="LoaderIcon" className="h-4 w-4 animate-spin" />;
      case BatchStatusEnum.Completed:
        return <Icon name="CircleCheckIcon" className="h-4 w-4" />;
      case BatchStatusEnum.Failed:
        return <Icon name="CircleXIcon" className="h-4 w-4" />;
      case BatchStatusEnum.PartiallyCompleted:
        return <Icon name="TriangleAlertIcon" className="h-4 w-4" />;
      case BatchStatusEnum.Cancelled:
        return <Icon name="CircleXIcon" className="h-4 w-4" />;
      default:
        return <Icon name="CalendarClockIcon" className="h-4 w-4" />;
    }
  };

  return (
    <div className="w-96">
      <div className="border-gray-medium mb-3 border-b pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-blue-dark text-lg font-semibold">
            {t('modal.batchesList.panelTitle')}
          </h3>
          <div className="flex items-center gap-2">
            {batches.length > 0 && onRefresh && (
              <Tooltip title={t('common.refresh')}>
                <Button size="small" aria-label={t('a11y.refreshList')} onClick={onRefresh}>
                  <Icon name="RotateCwIcon" className="h-4 w-4" />
                </Button>
              </Tooltip>
            )}

            {hasFailedBatches && (
              <Tooltip title={t('modal.batchesList.cancelAllFailed')}>
                <Button
                  size="small"
                  danger
                  aria-label={t('a11y.cancelAllFailed')}
                  onClick={handleCancelAllFailed}
                >
                  <Icon name="TrashIcon" className="h-4 w-4" />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {batches.length === 0 ? (
        <div className="text-gray-dark py-8 text-center">
          {t('modal.batchesList.noActiveBatches')}
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto">
          <Listy
            items={batches}
            rowKey="id"
            classNames={{ item: clsx(LISTY_ITEM_NO_HOVER, 'p-0!') }}
            itemRender={(batch) => {
              const isActive =
                batch.status === BatchStatusEnum.Processing ||
                batch.status === BatchStatusEnum.Queued;
              const showCancel = canCancelBatch(batch);
              const isClickable =
                batch.status === BatchStatusEnum.Failed ||
                batch.status === BatchStatusEnum.Processing ||
                batch.status === BatchStatusEnum.Queued ||
                batch.status === BatchStatusEnum.PartiallyCompleted;

              const pending = batch.pendingFiles ?? 0;
              const processing = batch.processingFiles ?? 0;
              const processed = batch.processedFiles ?? 0;
              const failed = batch.failedFiles ?? 0;
              const canceled = batch.canceledFiles ?? 0;
              const hasCounters = batch.totalFiles > 0;

              return (
                <div
                  className={clsx('rounded-md px-0 py-3 transition-colors', {
                    'hover:bg-gray-light cursor-pointer': isClickable,
                  })}
                  onClick={() => isClickable && handleBatchClick(batch)}
                >
                  <ListyMetaRow
                    className="py-0"
                    avatar={
                      <Avatar
                        icon={<Icon name={getOperationIcon(batch.operation)} className="h-5 w-5" />}
                        shape="square"
                        className={clsx(
                          'text-blue-dark',
                          isActive ? 'bg-blue-dark/20 animate-pulse' : 'bg-gray-light',
                        )}
                      />
                    }
                    title={
                      <div className="flex flex-col">
                        <div className="flex items-start justify-between">
                          <span className="text-blue-dark text-sm font-medium">
                            {t(`modal.batchesList.operation.${batch.operation.toLowerCase()}`)}
                          </span>
                          <span className="text-gray-dark ml-2 text-xs">
                            {formatTimestamp(dateFromTicks(parseInt(batch.timeStamp, 10))) || '-'}
                          </span>
                        </div>
                        {getObjectNames(batch).length > 0 && (
                          <span
                            className="text-gray-dark mt-1 truncate text-xs"
                            title={getObjectNames(batch).join(', ')}
                          >
                            {getObjectNames(batch).join(', ')}
                          </span>
                        )}
                      </div>
                    }
                    description={
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`flex items-center gap-1 text-xs ${getStatusColor(
                              batch.status,
                            )}`}
                          >
                            {getStatusIcon(batch.status)}
                            {t(`modal.batchesList.status.${batch.status.toLowerCase()}`)}
                          </span>
                          {showCancel && (
                            <Tooltip title={t('modal.batchesList.cancelBatch')}>
                              <Button
                                size="small"
                                danger
                                onClick={(e) => handleCancelBatch(e, batch)}
                              >
                                <Icon name="XIcon" className="h-3 w-3" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>

                        {hasCounters && (
                          <div className="text-gray-dark flex flex-wrap gap-x-3 gap-y-1 text-xs">
                            <span>{t('modal.batchesList.pendingFiles', { count: pending })}</span>
                            <span>
                              {t('modal.batchesList.processingFiles', { count: processing })}
                            </span>
                            <span>
                              {t('modal.batchesList.processedFiles', { count: processed })}
                            </span>
                            {failed > 0 && (
                              <span className="text-red-dark">
                                {t('modal.batchesList.failedFiles', { count: failed })}
                              </span>
                            )}
                            {canceled > 0 && (
                              <span>
                                {t('modal.batchesList.canceledFiles', { count: canceled })}
                              </span>
                            )}
                          </div>
                        )}

                        {isClickable && (
                          <div className="text-gray-dark flex items-center gap-1 text-xs">
                            {t('modal.batchesList.viewDetail')}
                            <Icon name="ChevronRightIcon" className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}
